/**
 * CG payroll / QuickBooks bill math aligned with Caregivers Summary Report
 * (appointmentPayrollSplit + non-OT, non-charge adjustments only).
 */
import { AppointmentModel, PaymentAdjustmentModel, PaymentAdjustmentTypes } from '../../appointmentModel';
import { CgPaymentClass, PaymentLineItemModel } from '../../PaymentIntentModel';
import { PricingUtil } from './pricing.util';

export interface CgBillAppointmentTotals {
  amount: number;
  hours: number;
  subsidizedHours: number;
  subsidizedAmount: number;
  adjustments: PaymentAdjustmentModel[];
  adjustmentsAmount: number;
}

export function getOvertimeHoursForCgPayroll(appt: AppointmentModel): number {
  let overtimeHours = 0;
  for (const adj of appt.adjustments ?? []) {
    if (adj.type === PaymentAdjustmentTypes.overtime) {
      overtimeHours += (adj.hours ?? 0) + (adj.minutes ?? 0) / 60;
    }
  }
  return PricingUtil.roundToTwoDecimals(overtimeHours);
}

/**
 * Same as caregiver-summary-report / checks: total payroll = check-in/out + OT hours;
 * subsidized cap from paidSubsidizedHours.
 */
export function appointmentPayrollSplitForCgBill(appt: AppointmentModel): {
  totalPayrollHours: number;
  subsidizedHours: number;
  regularHours: number;
} {
  const baseH = appt.checkInOut?.totalHours ?? 0;
  const otH = getOvertimeHoursForCgPayroll(appt);
  const totalPayrollHours = baseH + otH;
  if (
    !appt.loRates?.subsidized ||
    appt.paidSubsidizedHours == null ||
    appt.paidSubsidizedHours <= 0
  ) {
    return {
      totalPayrollHours,
      subsidizedHours: 0,
      regularHours: totalPayrollHours,
    };
  }
  const cap = appt.paidSubsidizedHours;
  const subsidizedHours = Math.min(cap, totalPayrollHours);
  const regularHours = Math.max(0, totalPayrollHours - subsidizedHours);
  return { totalPayrollHours, subsidizedHours, regularHours };
}

/** Matches caregiver-summary-report sumAmountPerAppointment adjustment filter (OT in hour split). */
export function isCgCostAdjustmentLineType(adj: PaymentAdjustmentModel): boolean {
  return (
    adj.type !== PaymentAdjustmentTypes.overtime &&
    adj.type !== PaymentAdjustmentTypes.chargeAdjustment
  );
}

/** Existing QuickBooks rule: only items not yet paid/billed on the adjustment row. */
export function shouldIncludeAdjustmentInQuickBooksBill(adj: PaymentAdjustmentModel): boolean {
  return !adj.paid && !adj.billed;
}

export function computeCgAppointmentTotalsForBill(
  appts: AppointmentModel[],
): CgBillAppointmentTotals {
  let amount = 0;
  let hours = 0;
  let subsidizedHours = 0;
  let subsidizedAmount = 0;
  const adjustments: PaymentAdjustmentModel[] = [];
  let adjustmentsAmount = 0;

  for (const appt of appts) {
    if (
      !appt.checkInOut ||
      appt.checkInOut.totalHours === undefined ||
      appt.checkInOut.totalHours === null
    ) {
      continue;
    }

    const split = appointmentPayrollSplitForCgBill(appt);
    const isSubsidizedLo =
      !!appt.loRates?.subsidized && (appt.paidSubsidizedHours ?? 0) > 0;

    if (isSubsidizedLo) {
      const sr = Number(appt.loRates?.subsidizedRate ?? 0);
      const subsidizedRate = isNaN(sr) ? 0 : sr;
      const regRate = appt.loRates?.rate ?? appt.hourlyCost ?? 0;
      subsidizedHours += split.subsidizedHours;
      subsidizedAmount += split.subsidizedHours * subsidizedRate;
      hours += split.regularHours;
      amount += split.regularHours * regRate;
    } else {
      amount += split.totalPayrollHours * (appt.hourlyCost ?? 0);
      hours += split.totalPayrollHours;
    }

    for (const adjustment of appt.adjustments ?? []) {
      if (
        shouldIncludeAdjustmentInQuickBooksBill(adjustment) &&
        isCgCostAdjustmentLineType(adjustment)
      ) {
        adjustments.push(adjustment);
        adjustmentsAmount += PricingUtil.total(adjustment.cost);
      }
    }
  }

  return { amount, hours, subsidizedHours, subsidizedAmount, adjustments, adjustmentsAmount };
}

export function adjustmentDescriptionForCgBillLine(adj: PaymentAdjustmentModel): string {
  if (adj.type === PaymentAdjustmentTypes.costAdjustment) {
    return adj.description || 'Cost Adjustment';
  }
  return adj.description ? `${adj.type} - ${adj.description}` : String(adj.type);
}

export function buildPaymentLineItemsFromTotals(
  totals: CgBillAppointmentTotals,
  lovedOneName: string,
): PaymentLineItemModel[] {
  const lineItems: PaymentLineItemModel[] = [];

  if (totals.hours > 0) {
    const displayRegularRate = PricingUtil.roundToTwoDecimals(totals.amount / totals.hours);
    lineItems.push({
      desc: `${totals.hours} hours @ $${displayRegularRate}/hour, Client: ${lovedOneName}`,
      amount: PricingUtil.toCurrency(totals.amount),
    });
  }

  if (totals.subsidizedHours > 0 && totals.subsidizedAmount > 0) {
    const effectiveSubsidizedRate = totals.subsidizedAmount / totals.subsidizedHours;
    if (isNaN(effectiveSubsidizedRate) || !isFinite(effectiveSubsidizedRate)) {
      throw new Error(
        `Cannot build QuickBooks bill line items: invalid subsidized rate (${totals.subsidizedAmount} / ${totals.subsidizedHours})`,
      );
    }
    lineItems.push({
      desc: `${totals.subsidizedHours} hours @ $${effectiveSubsidizedRate.toFixed(2)}/hour (SUBSIDIZED), Client: ${lovedOneName}`,
      amount: PricingUtil.toCurrency(totals.subsidizedAmount),
    });
  }

  for (const adjustment of totals.adjustments) {
    const adjustmentAmount = PricingUtil.total(adjustment.cost);
    const adjustmentDescription = adjustmentDescriptionForCgBillLine(adjustment);
    lineItems.push({
      desc: `${adjustmentDescription}, Client: ${lovedOneName}`,
      amount: PricingUtil.toCurrency(adjustmentAmount),
    });
  }

  return lineItems;
}

export function groupAppointmentsByCsAndLovedOne(
  appointments: AppointmentModel[],
): Map<string, Map<string, AppointmentModel[]>> {
  const grouped: Map<string, Map<string, AppointmentModel[]>> = new Map();
  for (const appt of appointments) {
    if (!grouped.has(appt.careseekerName)) {
      grouped.set(appt.careseekerName, new Map());
    }
    const careSeekerMap = grouped.get(appt.careseekerName)!;
    const loName =
      appt.lovedOnesPassports[0].LovedOnePersonalInfo.Name.FirstAndLast;
    if (!careSeekerMap.has(loName)) {
      careSeekerMap.set(loName, []);
    }
    careSeekerMap.get(loName)!.push(appt);
  }
  return grouped;
}

export function buildPaymentLineItemsForPayment(payment: CgPaymentClass): PaymentLineItemModel[] {
  const grouped = groupAppointmentsByCsAndLovedOne(payment.appointments);
  const items: PaymentLineItemModel[] = [];

  for (const cs of grouped.keys()) {
    const csMap = grouped.get(cs)!;
    const firstAppt = csMap.values().next().value[0];
    const csId = firstAppt.careseeker;

    for (const lo of csMap.keys()) {
      const appts = csMap.get(lo)!;
      const totals = computeCgAppointmentTotalsForBill(appts);
      items.push(...buildPaymentLineItemsFromTotals(totals, lo));

      const clis = payment.customLineItems[csId];
      if (clis) {
        for (const cli of clis) {
          if (!cli.paid) {
            items.push({
              desc: `${cli.type} - ${cli.desc}`,
              amount: PricingUtil.toCurrency(cli.amount.amount),
            });
          }
        }
      }
    }
  }

  return items;
}

/**
 * Appointment-derived bill amount only (no custom payment line items).
 * Uses unpaid/unbilled adjustment filter (same as QuickBooks lines).
 */
export function computeCgBillAppointmentsOnlyGrandTotal(
  payment: CgPaymentClass,
): number {
  let t = 0;
  const grouped = groupAppointmentsByCsAndLovedOne(payment.appointments);
  for (const cs of grouped.keys()) {
    const csMap = grouped.get(cs)!;
    for (const lo of csMap.keys()) {
      const appts = csMap.get(lo)!;
      const totals = computeCgAppointmentTotalsForBill(appts);
      t += totals.amount + totals.subsidizedAmount + totals.adjustmentsAmount;
    }
  }
  return PricingUtil.roundToTwoDecimals(t);
}

export function computeCgBillGrandTotalNumericForPayment(payment: CgPaymentClass): number {
  let t = 0;
  const grouped = groupAppointmentsByCsAndLovedOne(payment.appointments);

  for (const cs of grouped.keys()) {
    const csMap = grouped.get(cs)!;
    const firstAppt = csMap.values().next().value[0];
    const csId = firstAppt.careseeker;

    for (const lo of csMap.keys()) {
      const appts = csMap.get(lo)!;
      const totals = computeCgAppointmentTotalsForBill(appts);
      t += totals.amount + totals.subsidizedAmount + totals.adjustmentsAmount;

      const clis = payment.customLineItems[csId];
      if (clis) {
        for (const cli of clis) {
          if (!cli.paid) {
            t += cli.amount.amount;
          }
        }
      }
    }
  }

  return PricingUtil.roundToTwoDecimals(t);
}

/**
 * Port of caregiver-summary-report sumAmountPerAppointment for CG cost (useCharge=false).
 * Keeps display totals and bill preview aligned.
 */
export function sumCgCostAmountForAppointments(
  appts: AppointmentModel[],
  subsidized: boolean,
  includeAdjustments = true,
): number {
  let total = 0;
  for (const appt of appts) {
    const split = appointmentPayrollSplitForCgBill(appt);
    const isSubsidizedLo =
      !!appt.loRates?.subsidized && (appt.paidSubsidizedHours ?? 0) > 0;

    if (isSubsidizedLo) {
      if (subsidized) {
        const rate =
          appt.loRates?.subsidizedRate != null ? appt.loRates.subsidizedRate : 0;
        total += split.subsidizedHours * rate;
      } else {
        const rate = appt.loRates?.rate ?? appt.hourlyCost ?? 0;
        total += split.regularHours * rate;
      }
    } else if (!subsidized) {
      const rate = appt.hourlyCost ?? 0;
      total += split.totalPayrollHours * rate;
    }
  }
  if (!subsidized && includeAdjustments) {
    for (const appt of appts) {
      for (const adj of appt.adjustments ?? []) {
        if (isCgCostAdjustmentLineType(adj)) {
          total += adj.cost?.amount ?? 0;
        }
      }
    }
  }
  return PricingUtil.roundToTwoDecimals(total);
}

const TOTALS_MATCH_EPSILON = 0.02;

/**
 * Same aggregation as Caregivers Summary Report `cgTotal` per care seeker:
 * getCostPerCs(false) + getCostPerCs(true), all adjustment rows (no paid/billed filter).
 */
export function computeCgSummaryReportStyleTotalForAppointments(
  appointments: AppointmentModel[],
): number {
  const byCsId = new Map<string, AppointmentModel[]>();
  for (const a of appointments) {
    const key = (a.careseeker != null && a.careseeker !== '')
      ? a.careseeker
      : a.careseekerName ?? '';
    if (!byCsId.has(key)) {
      byCsId.set(key, []);
    }
    byCsId.get(key)!.push(a);
  }
  let total = 0;
  for (const appts of byCsId.values()) {
    total +=
      sumCgCostAmountForAppointments(appts, false, true) +
      sumCgCostAmountForAppointments(appts, true, true);
  }
  return PricingUtil.roundToTwoDecimals(total);
}

export function cgBillAppointmentsMatchSummaryReport(
  payment: CgPaymentClass,
): { match: boolean; delta: number } {
  const summaryTotal = computeCgSummaryReportStyleTotalForAppointments(
    payment.appointments,
  );
  const billApptTotal = computeCgBillAppointmentsOnlyGrandTotal(payment);
  const delta = PricingUtil.roundToTwoDecimals(billApptTotal - summaryTotal);
  return {
    match: Math.abs(delta) <= TOTALS_MATCH_EPSILON,
    delta,
  };
}
